import { useState } from "react";
import { Phone, MapPin, CheckCircle2 } from "lucide-react";
import { Reveal, ChapterHeading } from "@/components/Reveal";
import { STATES, BRAND } from "@/constants/states";

const STEPS = [
  {
    num: "01",
    title: "Tell Us What Happened",
    text: "Submit the referral form or call 928-605-5841. It takes 90 seconds. Confidential, zero pressure.",
    testId: "process-step-1",
  },
  {
    num: "02",
    title: "Curated Resource Mobilization",
    text: "Within minutes we activate the exact attorney, doctor, or body shop matched to your location and severity.",
    testId: "process-step-2",
  },
  {
    num: "03",
    title: "Recovery & Sovereign Resolution",
    text: "You receive care and representation while our team tracks every communication until resolution.",
    testId: "process-step-3",
  },
];

export const Process = () => {
  const [state, setState] = useState("");
  return (
    <section id="process" data-testid="process-section" className="scroll-mt-24 py-24 sm:py-32 px-4 sm:px-8">
      <div className="max-w-7xl mx-auto bg-[#071A2B]/40 rounded-3xl border border-[#C9A24B]/15 px-6 sm:px-12 py-16 sm:py-20">
        <ChapterHeading
          eyebrow="03 // SIMPLE & STRESS-FREE"
          title="From Impact to Empowerment in Three Direct Steps"
          eyebrowTestId="process-chapter-num"
          titleTestId="process-headline"
        />
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.12}>
              <div data-testid={s.testId} className="relative h-full group">
                <p className="font-display font-black text-6xl text-[#C9A24B]/15 group-hover:text-[#C9A24B]/30 transition-colors duration-500">
                  {s.num}
                </p>
                <div className="mt-2 gold-hairline w-16" />
                <h3 className="mt-5 font-editorial font-semibold text-xl text-[#FBF8F2]">{s.title}</h3>
                <p className="mt-3 text-sm text-[#8EA3B0] leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-col md:flex-row md:items-center gap-6 border-t border-[#C9A24B]/15 pt-10">
            <div className="flex items-center gap-3 shrink-0">
              <MapPin className="w-5 h-5 text-[#C9A24B]" />
              <p className="font-data text-xs tracking-[0.22em] uppercase text-[#D4DFE6]">Check Your State</p>
            </div>
            <select
              data-testid="process-state-checker"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="bg-[#092237] border border-[#C9A24B]/30 text-[#FBF8F2] text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#C9A24B] w-full md:w-72"
            >
              <option value="">Select your state&hellip;</option>
              {STATES.map((s) => (
                <option key={s} value={s}>
                  {s === "Arizona" ? "Arizona (Headquarters)" : s}
                </option>
              ))}
            </select>
            {state && (
              <p data-testid="process-state-result" className="flex items-center gap-2 text-sm text-[#5FC3CC]">
                <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                Covered &mdash; we have vetted partners in {state}.
                {state === "Arizona" && " Our home turf."}
              </p>
            )}
            <a
              href={BRAND.phoneHref}
              className="md:ml-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-[#E8C87A] hover:text-[#C9A24B] transition-colors duration-300"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
